import React, {Component, PropTypes} from 'react';
import {
  Button,
  Form,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Modal,
  HelpBlock,
} from '@sketchpixy/rubix';
import NumberFormat from 'react-number-format';
import styled from 'styled-components';
import {ClipLoader} from 'react-spinners';
import {css} from 'react-emotion';

import InputValidationType from '../../../constants/InputValidationType';
import CurrencyType from '../../../constants/CurrencyType';
import {refund} from '../../../api/api';
import RefundReasonType from '../../../constants/RefundReasonType';

const override = css`
  margin: auto 8px auto auto;
`;

const ButtonText = styled.div`
  display: flex;
`;

const AmountContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AmountErrorText = styled(HelpBlock)`
  display: ${props => props.isShow ? 'block' : 'none'};
`;

class RefundModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      floatAmount: 0,
      amount: 0,
      reason: '',
      validationState: {
        amount: InputValidationType.NULL,
        reason: InputValidationType.NULL,
      },
      loading: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    const scientificNotation = Math.pow(10, this.props.currencyType.decimalScale * -1);
    const floatAmount = nextProps.amount * scientificNotation;

    this.state = {
      floatAmount,
      amount: nextProps.amount,
      reason: '',
      validationState: {
        amount: InputValidationType.NULL,
        reason: InputValidationType.NULL,
      },
      loading: false,
    };
  }

  amountChangeHandler = (values) => {
    const {floatValue} = values;
    const scientificNotation = Math.pow(10, this.props.currencyType.decimalScale);
    const amount = floatValue * scientificNotation;

    let validationState = this.state.validationState;
    validationState.amount = InputValidationType.NULL;

    this.setState({
      floatAmount: floatValue,
      amount,
      validationState,
    });
  };

  reasonTypeChangeHandler = (event) => {
    let validationState = this.state.validationState;
    validationState.reason = InputValidationType.NULL;

    this.setState({
      reason: event.target.value,
      validationState,
    });
  };

  checkInputValidation() {
    let validationState = this.state.validationState;
    let isValid = true;
    if (0 < this.state.amount && this.state.amount <= this.props.amount) {
      validationState.amount = InputValidationType.SUCCESS;
    } else {
      validationState.amount = InputValidationType.ERROR;
      isValid = false;
    }

    if (this.state.reason) {
      validationState.reason = InputValidationType.SUCCESS;
    } else {
      validationState.reason = InputValidationType.ERROR;
      isValid = false;
    }

    this.setState({
      validationState,
    });

    return isValid;
  }

  refund = () => {
    const isValid = this.checkInputValidation();

    if (isValid) {
      this.setState({
        loading: true,
      });

      refund(this.props.paymentId, this.state.amount, this.state.reason, (err, result) => {
        this.setState({
          loading: false,
        });

        if (err) {
          Messenger().post({
            type: 'error',
            message: `${err.message} (${err.statusCode})`,
            showCloseButton: true,
          });
        } else {
          if ((result.statusCode === '0' || result.statusCode === 0) && result.refund.status === 'succeeded') {
            this.props.closeModal(true);
          } else {
            Messenger().post({
              type: 'error',
              message: `Refund failed.`,
              showCloseButton: true,
            });
          }
        }
      });
    }
  };

  render() {
    return (
        <Modal
            show={this.props.showModal}
            onHide={this.props.closeModal}
            backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Refund payment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form horizontal>
              <FormGroup validationState={this.state.validationState.amount.validationName}>
                <Col xs={3} componentClass={ControlLabel}>
                  Refund
                </Col>
                <Col xs={9}>
                  <AmountContainer>
                    <NumberFormat
                        className="form-control"
                        thousandSeparator
                        prefix={`${this.props.currency} `}
                        decimalScale={this.props.currencyType.decimalScale}
                        fixedDecimalScale
                        allowNegative={false}
                        value={this.state.floatAmount}
                        isAllowed={(values) => {
                          const {floatValue} = values;
                          const scientificNotation = Math.pow(10, this.props.currencyType.decimalScale);
                          const value = floatValue * scientificNotation;
                          return value <= Number.MAX_SAFE_INTEGER;
                        }}
                        onValueChange={(values) => this.amountChangeHandler(values)}
                    />
                    <NumberFormat
                        displayType={'text'}
                        thousandSeparator
                        decimalScale={this.props.currencyType.decimalScale}
                        fixedDecimalScale
                        allowNegative={false}
                        value={this.state.floatAmount}
                        renderText={value => {
                          let moreThan = '';
                          const scientificNotation = Math.pow(10, this.props.currencyType.decimalScale * -1);
                          let amount = this.props.amount * scientificNotation;
                          if (this.state.amount > this.props.amount) {
                            moreThan = ' more than';
                          }
                          if (this.state.amount === 0) {
                            amount = value;
                          }
                          const errorText = `Refund cannot be${moreThan} ${this.props.currency}${amount}`;
                          return (
                              <AmountErrorText isShow={this.state.validationState.amount === InputValidationType.ERROR}>
                                {errorText}
                              </AmountErrorText>
                          );
                        }}
                    />
                  </AmountContainer>
                </Col>
              </FormGroup>
              <FormGroup validationState={this.state.validationState.reason.validationName}>
                <Col xs={3} componentClass={ControlLabel}>
                  Reason
                </Col>
                <Col xs={9}>
                  <FormControl
                      componentClass="select"
                      placeholder="select"
                      value={this.state.reason}
                      onChange={this.reasonTypeChangeHandler}
                  >
                    <option disabled>Select a reason</option>
                    {
                      RefundReasonType.enumValues.map(refundReasonType => (
                          <option key={refundReasonType.value} value={refundReasonType.value}>
                            {refundReasonType.title}
                          </option>
                      ))
                    }
                  </FormControl>
                </Col>
              </FormGroup>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.props.closeModal}>Cancel</Button>
            <NumberFormat
                displayType={'text'}
                thousandSeparator
                prefix={`${this.props.currency}`}
                decimalScale={this.props.currencyType.decimalScale}
                fixedDecimalScale
                allowNegative={false}
                value={this.state.floatAmount}
                renderText={value => {
                  let buttonName = `Refund ${value}`;
                  if (this.state.amount === this.props.amount) {
                    buttonName = 'Refund';
                  }

                  return (
                      <Button bsStyle="brownishgray" onClick={this.refund} disabled={this.state.loading}>
                        <ButtonText>
                          <ClipLoader
                              className={override}
                              sizeUnit={'px'}
                              size={12}
                              color={'#123abc'}
                              loading={this.state.loading}
                          />
                          <span>
                            {buttonName}
                          </span>
                        </ButtonText>
                      </Button>
                  );
                }}
            />
          </Modal.Footer>
        </Modal>
    );
  }
}

RefundModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  paymentId: PropTypes.number.isRequired,
  amount: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  currencyType: PropTypes.instanceOf(CurrencyType).isRequired,
};

export default RefundModal;



// WEBPACK FOOTER //
// ./src/routes/patient/RefundModal/RefundModal.js